import { useCallback, useEffect, useState } from "react";
import { messageStore } from "@/modules/store/messageStore";
import { conversationService } from "@/modules/services/conversationService";
import {
  Attachment,
  Message,
  MessageCreate,
} from "@/modules/types/conversation/message";
import FileResizer from "react-image-file-resizer";
import { AppError } from "@/lib/utils/errors";
import { enqueueSnackbar } from "notistack";

export const useMessageOfConversation = (conversationId: string) => {
  const { messageOfConversation, setMessages, appendMessages, setLoading } =
    messageStore();

  const state = messageOfConversation[conversationId] || {
    messages: [],
    hasMore: true,
    nextId: undefined,
    loading: false,
  };

  const fetchMessages = useCallback(
    async (isNext = false) => {
      if (state.loading || (!state.hasMore && isNext)) return;
      setLoading(conversationId, true);
      try {
        const resp = await conversationService.getMessageByConversationId(
          conversationId,
          20,
          isNext ? state.nextId : undefined
        );
        if (isNext) {
          appendMessages(
            conversationId,
            resp.messages || [],
            !!resp.nextId,
            resp.nextId
          );
        } else {
          setMessages(
            conversationId,
            resp.messages || [],
            !!resp.nextId,
            resp.nextId
          );
        }
      } finally {
        setLoading(conversationId, false);
      }
    },
    [conversationId, state.nextId, state.hasMore, state.loading]
  );

  useEffect(() => {
    if (!state.messages.length) {
      fetchMessages(false);
    }
  }, [conversationId]);

  const getMessages = useCallback(() => {
    if (state.hasMore && !state.loading) {
      fetchMessages(true);
    }
  }, [fetchMessages, state.hasMore, state.loading]);

  return {
    messages: state.messages,
    hasMore: state.hasMore,
    dataLength: state.messages.length,
    getMessages,
    loading: state.loading,
  };
};

export const useInputMessage = (
  conversationId: string,
  userId: string,
  userName: string,
  onMessageSent?: (message: Message) => void
) => {
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resizeFile = (fileToResize: File) =>
    new Promise<File>((resolve) => {
      const cropPercentage = fileToResize.size > 900000 ? 30 : 70;
      FileResizer.imageFileResizer(
        fileToResize,
        500,
        500,
        "JPEG",
        cropPercentage,
        0,
        (file) => {
          const fileData = file as File;
          if (fileData) {
            resolve(fileData);
          }
        },
        "file"
      );
    });

  const sendMessage = useCallback(
    async (content: string, files?: File[], replyToMessageId?: string) => {
      if (!content.trim() && (!files || files.length === 0)) {
        setError("Message cannot be empty");
        return;
      }

      setSending(true);
      setError(null);

      try {
        // First, upload attachments if any
        let attachments: Attachment[] = [];

        if (files && files.length > 0) {
          // This would be where you'd upload files and get URLs back
          // For example:
          // attachments = await uploadFiles(files);

          // Placeholder implementation - replace with actual file upload
          attachments = await Promise.all(
            Array.from(files).map(async (file) => {
              // In a real implementation, you would upload the file and get back a URL
              // This is a mock implementation
              let fileData = file;
              if (file.type.includes("image")) {
                fileData = await resizeFile(file);
              }
              const randomId = Math.random().toString(36).substring(2, 15);
              const fileName = `${randomId}_${file.name}`;
              const url = await conversationService.uploadFile(
                fileData,
                conversationId,
                fileName
              );
              return {
                type: file.type.startsWith("image/") ? "image" : "file",
                url: url, // This is temporary and won't work in production
                fileName: fileName,
                fileSize: file.size,
                contentType: file.type,
                messageId: "", // This will be filled in by the server
              };
            })
          );
        }

        // Create the message object
        const messageData: MessageCreate = {
          conversationId,
          senderId: userId,
          senderName: userName,
          content,
          attachments: attachments.length > 0 ? attachments : undefined,
          replyTo: replyToMessageId,
        };

        // Send the message
        const response = await conversationService.sendMessage(messageData);

        if (response.messageData) {
          // Call the callback with the new message
          onMessageSent?.(response.messageData);
          return response.messageData;
        } else {
          throw new Error("Failed to send message: No message data returned");
        }
      } catch (err) {
        const appErr = new AppError(err);
        appErr.setAction("useMessage.sendMessage");
        appErr.setApiAction("conversationService.sendMessage");
        appErr.logError();
        const errorMessage = appErr.message || "Failed to send message";
        setError(errorMessage);
        enqueueSnackbar(errorMessage, { variant: "error" });
      } finally {
        setSending(false);
      }
    },
    [conversationId, userId, userName, onMessageSent]
  );

  return {
    sendMessage,
    sending,
    error,
  };
};

export const useMessage = () => {
  const { messageOfConversation, appendMessages, setLoading, setMessages } =
    messageStore();

  const fetchMessages = async (conversationInfo: {
    conversationId: string;
    isNext: boolean;
    loading: boolean;
    hasMore: boolean;
    nextId?: string;
  }) => {
    if (
      conversationInfo.loading ||
      (!conversationInfo.hasMore && conversationInfo.isNext)
    )
      return;
    setLoading(conversationInfo.conversationId, true);
    try {
      const resp = await conversationService.getMessageByConversationId(
        conversationInfo.conversationId,
        20,
        conversationInfo.isNext ? conversationInfo.nextId : undefined
      );
      if (conversationInfo.isNext) {
        appendMessages(
          conversationInfo.conversationId,
          resp.messages || [],
          !!resp.nextId,
          resp.nextId
        );
      } else {
        setMessages(
          conversationInfo.conversationId,
          resp.messages || [],
          !!resp.nextId,
          resp.nextId
        );
      }
    } finally {
      setLoading(conversationInfo.conversationId, false);
    }
  };

  const appendMessage = (conversationId: string, message: Message) => {
    if (
      messageOfConversation[conversationId] &&
      messageOfConversation[conversationId].messages.length > 0
    ) {
      appendMessages(
        conversationId,
        [message],
        messageOfConversation[conversationId].hasMore,
        messageOfConversation[conversationId].nextId
      );
    } else {
      fetchMessages({
        conversationId,
        isNext: false,
        loading: false,
        hasMore: true,
        nextId: undefined,
      });
    }
  };
  return {
    appendMessage,
  };
};
